// [S-721] Marketplace data hooks. Backed by the admin REST API added in
// S-719/S-720/S-721: `GET /tenants/:tenantId/marketplace-listings` (browse,
// enriched with platform-level stats), `POST .../:listingId/import`
// (S-720, unchanged), `POST .../:listingId/rating` (S-721, anonymous —
// see MarketplaceService.rate's doc comment in packages/core).
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type MarketplaceListingFilter = {
  dealType?:
    | "feature_film"
    | "tv_series"
    | "streaming"
    | "documentary"
    | "commercial"
    | "short_form";
  guild?: "SAG" | "DGA" | "WGA" | "IATSE" | "AFM" | "PGA";
  tier?: "basic" | "professional" | "enterprise";
  documentForm?: "deal_memo" | "long_form";
};

// Mirrors MarketplaceListingWithStats (packages/core/src/services/
// MarketplaceService.ts) as returned by serializeTemplate-free JSON — the
// route comment confirms no Date fields, so no ISO-string conversion here.
export type MarketplaceListingRow = {
  id: string;
  title: string | null;
  dealType: MarketplaceListingFilter["dealType"];
  guild: MarketplaceListingFilter["guild"];
  tier: MarketplaceListingFilter["tier"];
  documentForm: MarketplaceListingFilter["documentForm"] | null;
  content: string;
  publisherId: string | null;
  pricingModel: "free";
  status: "published" | "delisted";
  sourceTemplateId?: string;
  averageRating: number | null;
  ratingCount: number;
  importCount: number;
};

function toQueryString(filter: MarketplaceListingFilter): string {
  const params = new URLSearchParams();
  if (filter.dealType !== undefined) params.set("dealType", filter.dealType);
  if (filter.guild !== undefined) params.set("guild", filter.guild);
  if (filter.tier !== undefined) params.set("tier", filter.tier);
  if (filter.documentForm !== undefined) params.set("documentForm", filter.documentForm);
  const s = params.toString();
  return s.length === 0 ? "" : `?${s}`;
}

export const MARKETPLACE_LISTINGS_KEY = ["marketplace", "listings"] as const;

export function useMarketplaceListings(
  tenantId: string,
  filter: MarketplaceListingFilter
): UseQueryResult<MarketplaceListingRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...MARKETPLACE_LISTINGS_KEY, tenantId, filter] as const,
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const res = await api<{ listings: MarketplaceListingRow[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/marketplace-listings${toQueryString(filter)}`
      );
      return res.listings;
    },
  });
}

export type ImportListingInput = { tenantId: string; listingId: string };

// Returns the resulting L2 draft's id — enough for the "View in Template
// Management" link, without pulling in the full LifecycleTemplate wire
// type just for this one screen.
export function useImportListing(): UseMutationResult<
  { id: string },
  Error,
  ImportListingInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, listingId }) =>
      api<{ id: string }>(
        `/tenants/${encodeURIComponent(tenantId)}/marketplace-listings/${encodeURIComponent(listingId)}/import`,
        { method: "POST" }
      ),
    onSuccess: (_data, { tenantId }) => {
      // Import count is anonymous/aggregate but still real — refetch so the
      // card's importCount reflects this submission.
      void qc.invalidateQueries({ queryKey: [...MARKETPLACE_LISTINGS_KEY, tenantId] });
      // The tenant's own L2 template list (used to derive per-listing
      // "already imported" state — see Marketplace.tsx) also just changed.
      void qc.invalidateQueries({ queryKey: ["templates", "admin", "list", tenantId] });
    },
  });
}

export type RateListingInput = { tenantId: string; listingId: string; rating: number };

// `recorded: false` means this caller already voted on this listing (one
// vote per person, server-enforced — see MarketplaceService.rate). The
// screen uses this to lock the control either way: recorded or not, the
// caller has now used their one vote.
export function useRateListing(): UseMutationResult<
  { recorded: boolean },
  Error,
  RateListingInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, listingId, rating }) =>
      api<{ recorded: boolean }>(
        `/tenants/${encodeURIComponent(tenantId)}/marketplace-listings/${encodeURIComponent(listingId)}/rating`,
        { method: "POST", body: { rating } }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...MARKETPLACE_LISTINGS_KEY, tenantId] });
    },
  });
}
